// Page 3 of the agreement: Color Selection and Certificate of Completion.
import { SignaturePad } from "@/components/forms/SignaturePad";
import { InlineField, CheckRow, SectionTitle } from "./AgreementFields";
import { Label } from "@/components/ui/label";
import { type RBAgreementData, SHINGLE_COLORS } from "./agreementTypes";

interface Props {
  data: RBAgreementData;
  update: <K extends keyof RBAgreementData>(k: K, v: RBAgreementData[K]) => void;
  logoUrl: string | null;
}

export function AgreementPage3({ data, update, logoUrl }: Props) {
  return (
    <div className="bg-white text-neutral-900 px-12 py-12 shadow-sm rounded-lg print:shadow-none print:rounded-none">
      <div className="flex items-center justify-between border-b-4 border-neutral-900 pb-3 mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight">Homeowner Color Selection</h1>
        {logoUrl && <img src={logoUrl} alt="RB Restoration" className="w-20 h-20 object-contain" />}
      </div>

      <p className="text-sm text-neutral-800 leading-relaxed">
        Congratulations on your new roof! We know that for you it is more than a ceiling: it is an
        expression of your personality and style. Our representative will provide you with a color palette
        for you to choose your new roof color. Review the available shingle colors and use the form below
        to choose your selection. After that, we can schedule your roof replacement, get a delivery date,
        and get started.
      </p>

      <SectionTitle>I choose the color:</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {SHINGLE_COLORS.map((c) => (
          <CheckRow
            key={c}
            label={c}
            checked={data.color_choice === c}
            onChange={(v) => update("color_choice", v ? c : "")}
          />
        ))}
      </div>
      <div className="mt-4">
        <InlineField label="Other:" value={data.color_other} onChange={(v) => update("color_other", v)} />
      </div>

      <div className="flex items-baseline gap-3 mt-6 border-t pt-3">
        <p className="text-sm">For replacement of my roof.</p>
        <InlineField
          label="Initials:"
          value={data.color_initial}
          onChange={(v) => update("color_initial", v)}
          className="max-w-[12rem]"
        />
      </div>

      <SectionTitle>Certification of Completion of Repairs</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InlineField label="Claim #:" value={data.cert_claim_number} onChange={(v) => update("cert_claim_number", v)} />
        <InlineField label="Street Address:" value={data.cert_address} onChange={(v) => update("cert_address", v)} />
        <InlineField label="City:" value={data.cert_city} onChange={(v) => update("cert_city", v)} />
        <InlineField label="State:" value={data.cert_state} onChange={(v) => update("cert_state", v)} />
        <InlineField label="Zip Code:" value={data.cert_zip} onChange={(v) => update("cert_zip", v)} />
        <InlineField label="Type of Damage:" value={data.cert_damage_type} onChange={(v) => update("cert_damage_type", v)} />
        <InlineField label="Date of Loss:" type="date" value={data.cert_loss_date} onChange={(v) => update("cert_loss_date", v)} />
      </div>

      <p className="text-xs text-neutral-800 leading-relaxed mt-4">
        I/We hereby certify that all necessary repairs in connection with damage sustained to the property
        have been completed in a satisfactory manner by RB Restoration Builders and that no liens have been
        or will be filed against the property as a result of labor performed or material used. The above
        referenced property has now been restored to the condition existing prior to the date of damage.
        All contractors, subcontractors and suppliers who provided labor or materials in connection with
        the repairs to the property have been paid in full or will be paid from the final disbursement of
        Insurance.
      </p>

      <div className="mt-6">
        <Label className="text-sm font-semibold">Homeowner Signature</Label>
        <SignaturePad
          value={data.cert_homeowner_signature}
          onChange={(v) => update("cert_homeowner_signature", v)}
        />
      </div>

      <div className="mt-8 border-t pt-4 text-xs text-neutral-700 flex flex-col md:flex-row justify-between gap-2">
        <span>📍 6700 Orr Rd #101, Charlotte NC 28213</span>
        <span>✉️ rb@rbrestorationbuilders.com</span>
        <span>📞 (704) 284-5012</span>
      </div>
    </div>
  );
}
